import { format, isSameYear, setHours, setMinutes } from 'date-fns';
import { uniq, uniqBy, flatten, sortBy, orderBy } from 'lodash';
import { stringify } from 'qs-esm';

import { proxyGet } from '../utils/proxy';
import zonedDate from '../utils/zoned-date';

let _performancesPromise = null;
let _eventsPromise = null;
let _peoplePromise = null;
let _blogsPromise = null;
let _postsPromise = null;

const now = zonedDate();

export function getPerformances() {
    if (_performancesPromise) {
        return _performancesPromise;
    }

    _performancesPromise = proxyGet(`/event-instances.json`).then(response => {
        let performances = response.docs.map(doc => {
            doc = processKeywords(doc);
            doc = processPerformance(doc);

            doc._auxiliary = {
                group_by_day: format(doc.performancedate, 'yyyy-MM-dd'),
                group_by_month: format(doc.performancedate, 'yyyy-MM'),
                group_name_month: isSameYear(now, doc.performancedate) ? format(doc.performancedate, 'MMMM') : format(doc.performancedate, 'MMMM yyyy'),
            }

            return doc;
        });

        performances = performances.filter(item => {
            // Set the time to end of day. We still want events to appear all day on the day of the performance.
            const modifiedTimeDate = setMinutes(setHours(zonedDate(item.performancedate), 23), 59);

            return +modifiedTimeDate > +now;
        });

        return sortBy(performances, item => {
            return +item.performancedate;
        });
    });

    return _performancesPromise;
}

export function getEvents() {
    if (_eventsPromise) {
        return _eventsPromise;
    }

    _eventsPromise = getPerformances().then(performances => {
        let events = {}

        for (const performance of performances) {
            const event = performance.event;

            if ((event.id in events) === false) {
                events[event.id] = {
                    ...event,
                    filters: {
                        categories: [],
                        event_types: [],
                        venues: [],
                        event_series: [],
                        //event_seasons: [],
                        keywords: [],
                    },
                }
            }

            events[event.id].filters.categories.push(performance.filters.categories);
            events[event.id].filters.event_types.push(performance.filters.event_types);
            events[event.id].filters.venues.push(performance.filters.venues);
            events[event.id].filters.event_series.push(performance.filters.event_series);
            //events[event.id].filters.event_seasons.push(performance.filters.event_seasons);
            events[event.id].filters.keywords.push(performance.filters.keywords);
        }

        events = Object.values(events);

        events = events.map(event => {
            event.startDate = zonedDate(event.startDateLocalAsUTC);
            event.endDate = zonedDate(event.endDateLocalAsUTC);

            event.filters.categories = flatten(event.filters.categories);
            event.filters.event_types = flatten(event.filters.event_types);
            event.filters.venues = flatten(event.filters.venues);
            event.filters.event_series = flatten(event.filters.event_series);
            //event.filters.event_seasons = flatten(event.filters.event_seasons);
            event.filters.keywords = flatten(event.filters.keywords);

            event.filters.categories = uniq(event.filters.categories);

            event.filters.event_types = uniqBy(event.filters.event_types, item => {
                return item.title;
            });

            event.filters.venues = uniqBy(event.filters.venues, item => {
                return item.title;
            });

            event.filters.event_series = uniqBy(event.filters.event_series, item => {
                return item.title;
            });

            // event.filters.event_seasons = uniqBy(event.filters.event_seasons, item => {
            //     return item.title;
            // });

             event.filters.keywords = uniqBy(event.filters.keywords, item => {
                return item.title;
            })

            return event;
        });

        return sortBy(events, item => {
            return +item.startDate;
        });
    });

    return _eventsPromise;
}

export function getBlogs() {
    if (_blogsPromise) {
        return _blogsPromise
    }

    _blogsPromise = proxyGet('/blogs')
        .then(response => {
            return response.docs;
        });

    return _blogsPromise;
}

export async function getPosts(blogId) {
    if (_postsPromise) {
        return _postsPromise;
    }

    _postsPromise = new Promise(async (resolve) => { // eslint-disable-line
        const query = {
            limit: 100,
            blog: {
                equals: blogId,
            },
        }

        let docs = [];
        let page = 1;

        do {
            const stringifiedQuery = stringify({ ...query, page: page }, { addQueryPrefix: true });

            const response = await proxyGet(`/posts${stringifiedQuery}`);

            docs = [ ...docs, ...response.docs ];

            page = response.nextPage;
        } while (page);

        // @todo remove this once the `where` query param is working
        docs = docs.filter(doc => {
            return doc.blog?.id === blogId;
        });

        docs = docs.map(doc => {
            doc.publishDate = zonedDate(doc.publishDate);

            return doc;
        });

        docs = orderBy(docs, doc => {
            return +doc.publishDate;
        }, 'desc');

        resolve(docs);
    });

    return _postsPromise;
}

export async function getPeople() {
    if (_peoplePromise) {
        return _peoplePromise;
    }

    _peoplePromise = new Promise(async (resolve) => { // eslint-disable-line
        const query = {
            limit: 100,
        }

        let docs = [];
        let page = 1;

        do {
            const stringifiedQuery = stringify({ ...query, page: page }, { addQueryPrefix: true });

            const response = await proxyGet(`/people${stringifiedQuery}`);
            // const response = await fetch(`https://clevelandorchestra.mybasker.com/api/people?${params.toString()}`).then(r => r.json()); // eslint-disable-line

            docs = [ ...docs, ...response.docs ];

            page = response.nextPage;
        } while (page);

        resolve(docs);
    });

    return _peoplePromise;
}

/**
 * Mimic Shopify's handleize function
 *
 * https://github.com/Shopify/liquid/blob/42b6763546f218dc8ad1184e10c5e2d9a262b006/performance/shopify/shop_filter.rb#L97-L106
 */
function handleize(string) {
    if (!string) {
        return '';
    }

    return string
        .toString()
        .toLowerCase()
        .replace(/['"()[]]/g, '')
        .replace(/\W+/g, '-')
        .replace(/-+$/, '')
        .replace(/^-+/, '');
}

function processKeywords(bookableItem) {
    const categories = [];
    const eventTypes = [];
    //const eventSeasons = [];
    const eventSeries = [];
    const venues = [];
    let keywords = [];

    const eventTypesArray = bookableItem.attributes?.custom?.event_type ?? [];

    const season = bookableItem.event?.season?.title;
    const series = bookableItem.event?.series

    eventTypesArray.map(type => {
        categories.push(type);
    });

    categories.length ? categories.map(category => {
        eventTypes.push({ title: category });
    }) : [];

    bookableItem.availability_handle = handleize(bookableItem.availability);

    if (bookableItem.venue) {
        venues.push({ title: bookableItem.venue?.title });
    }

    // if (season) {
    //     eventSeasons.push({title: season})
    // }

    if (series) {
        series.forEach(s => {
            eventSeries.push({title: s.title});
        });
    }

    if (bookableItem.event.tessitura?.keywords) {
        bookableItem.event.tessitura.keywords.forEach(keyword => {
            if (keyword.Description) {
                keywords.push({ title: keyword.Description });
            }
        })
    }

    bookableItem.filters = {
        categories: categories,
        venues: venues,
        keywords: keywords,
        event_types: eventTypes,
        //event_seasons: eventSeasons,
        event_series: eventSeries,
    };

    return bookableItem;
}

function processPerformance(bookableItem) {
    bookableItem.venue = bookableItem.event.venue ? bookableItem.event.venue.name : null;
    bookableItem.title = bookableItem.event.title;
    bookableItem.prefix = bookableItem.event.prefix;
    bookableItem.performancedate = zonedDate(bookableItem.startDateLocalAsUTC);
    bookableItem.description = bookableItem.event.description;
    bookableItem.image = bookableItem.event?.image || null;
    bookableItem.availability = bookableItem.attributes?.custom?.availability || null;
    bookableItem.availability_handle = bookableItem.availability?.toLowerCase().replace(/\s/g, '-');

    if (bookableItem.booking?.bookingLinkURL) { // Custom booking link
        bookableItem.ctaPrimary = {
            title: bookableItem.booking.bookingLinkLabel ? bookableItem.booking.bookingLinkLabel : 'Buy Tickets',
            link: bookableItem.booking.bookingLinkURL
        };
    } else {
        bookableItem.ctaPrimary = {
            title: 'Buy Tickets',
            link: `${AppConfig.boDomain}booking/production/${bookableItem.tessitura.performanceId}` // Old setup: `${AppConfig.boDomain}${bookableItem.tessitura.productionSeasonId}/${bookableItem.tessitura.performanceId}`,
        };
    }

    bookableItem.ctaSecondary = bookableItem.event.url ? {
        title: 'More Info',
        link: bookableItem.event.url,
    } : null;

    const date = bookableItem.performancedate;
    const hour = format(date, 'h');
    const minutes = format(date, 'mm');
    const period = format(date, 'a').toLowerCase();

    bookableItem.formattedTime = minutes === '00' ? `${hour}${period}` : `${hour}:${minutes}${period}`;

    return bookableItem;
}
